import { useCallback, useEffect, useRef, useState } from "react";

export type AdminUser = {
  id: number;
  login: string;
  username: string | null;
  email: string | null;
  email_verified: boolean;
  disabled: boolean;
  roles: string[];
  created_at: string;
};

type UsersState = {
  users: AdminUser[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastUpdated: number | null;
};

export function useAdminUsers(enabled: boolean, reloadKey?: string) {
  const [state, setState] = useState<UsersState>({
    users: [],
    loading: false,
    refreshing: false,
    error: null,
    lastUpdated: null,
  });
  const requestIdRef = useRef(0);

  const load = useCallback(async (opts?: { bustCache?: boolean }) => {
    const requestId = ++requestIdRef.current;
    setState((s) => ({
      ...s,
      loading: s.users.length === 0,
      refreshing: s.users.length > 0,
      error: null,
    }));

    const url = opts?.bustCache
      ? `/api/admin/users?_=${Date.now()}`
      : "/api/admin/users";

    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (requestId !== requestIdRef.current) return;

      if (res.status === 401 || res.status === 403) {
        setState({
          users: [],
          loading: false,
          refreshing: false,
          error: "Not authorized",
          lastUpdated: null,
        });
        return;
      }
      if (!res.ok) {
        setState((s) => ({
          ...s,
          loading: false,
          refreshing: false,
          error: "Failed to load users",
        }));
        return;
      }

      const data = await res.json();
      if (requestId !== requestIdRef.current) return;

      setState({
        users: Array.isArray(data.users) ? data.users : [],
        loading: false,
        refreshing: false,
        error: null,
        lastUpdated: Date.now(),
      });
    } catch {
      if (requestId !== requestIdRef.current) return;
      setState((s) => ({
        ...s,
        loading: false,
        refreshing: false,
        error: "Failed to load users",
      }));
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current += 1;
      setState({
        users: [],
        loading: false,
        refreshing: false,
        error: null,
        lastUpdated: null,
      });
      return;
    }
    void load({ bustCache: true });
  }, [enabled, reloadKey, load]);

  const reload = useCallback(() => load({ bustCache: true }), [load]);

  return { ...state, reload };
}
